import type * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * The ruled head every titled block shares: page header, section, panel and
 * card (DESIGN.md "Ruled sections").
 *
 * The title sits on its satr, a 2px ink rule exactly as long as the title,
 * which continues as the 1px `--border` hairline across the head. The head
 * draws the hairline as its bottom border and aligns its children to it; the
 * title (marked `data-satr`) draws the satr as its own bottom border and
 * pulls 1px down so the satr lies ON the hairline. Every other child (meta,
 * links, controls) stands 10px above the rule.
 *
 * A title may also sit one level down, in a wrapper beside its meta; the
 * wrapper then takes no margin and its other children take the 10px.
 *
 * Below 900px the links and controls drop below the hairline instead of
 * pushing it away: the border becomes a full-width `::after` line ordered
 * after the title, and everything that is not the title is ordered after it.
 *
 * `data-satr` is the stable hook for the signature layer, which writes the
 * satr and the hairline on arrival. Nothing here animates.
 */
const RULED_HEAD = cn(
  'flex flex-wrap items-end gap-x-4 border-b border-border',
  '[&>:not([data-satr]):not(:has([data-satr]))]:mb-2.5',
  '[&>:has(>[data-satr])>:not([data-satr])]:mb-2.5',
  'max-[900px]:gap-y-0 max-[900px]:border-b-0',
  "max-[900px]:after:order-1 max-[900px]:after:h-px max-[900px]:after:basis-full max-[900px]:after:bg-border max-[900px]:after:content-['']",
  'max-[900px]:[&>:not([data-satr]):not(:has([data-satr]))]:order-2',
  'max-[900px]:[&>:not([data-satr]):not(:has([data-satr]))]:mt-2',
)

/** The satr under a title: the 2px ink rule, lying on the head's hairline. */
const SATR = 'relative z-[1] -mb-px border-b-2 border-foreground pb-[9px]'

/**
 * A named group of content on a screen: a title on its satr over the content.
 *
 * `Section` is not `Panel`. A panel is a ruled section that owns its rows (a
 * table, a list) and bleeds 20px past its text; a section is a heading over
 * content that may itself hold panels, tables or tile groups. Only the
 * section's head bleeds, so its content keeps the alignment it had.
 *
 * Spacing is the caller's: stacked sections sit 40px apart (`space-y-10`),
 * side-by-side sections 64px apart (`gap-x-gutter`), so their rules break in
 * the gutter.
 */
function Section({ className, ...props }: React.ComponentProps<'section'>) {
  return <section data-slot="section" className={cn('min-w-0', className)} {...props} />
}

/**
 * Title, then meta, then action, in that order. `SectionTitle` carries
 * `me-auto`, so it, and only it, pushes everything after it to the end side.
 */
function SectionHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="section-header"
      className={cn(RULED_HEAD, '-mx-bleed px-bleed', className)}
      {...props}
    />
  )
}

/**
 * `h2` by default, because the screen title is the `h1` and a section sits
 * under it. Alexandria 600 at 16px on its satr.
 */
function SectionTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return (
    <h2
      data-slot="section-title"
      data-satr=""
      className={cn(
        SATR,
        'text-section-title text-foreground me-auto min-w-0 break-words',
        className,
      )}
      {...props}
    />
  )
}

/**
 * The fact about the group that is not part of its name: "12 of 40 shown",
 * "oldest first".
 */
function SectionMeta({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="section-meta"
      className={cn('text-muted-foreground shrink-0 text-sm', className)}
      {...props}
    />
  )
}

/** End-aligned controls for this group only. The screen's own actions belong in `PageHeader`. */
function SectionAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="section-action"
      className={cn('flex shrink-0 flex-wrap items-center gap-2', className)}
      {...props}
    />
  )
}

/** 16px under the rule. */
function SectionBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="section-body"
      className={cn('animate-enter-stagger mt-4 min-w-0', className)}
      {...props}
    />
  )
}

export {
  RULED_HEAD,
  SATR,
  Section,
  SectionAction,
  SectionBody,
  SectionHeader,
  SectionMeta,
  SectionTitle,
}
